<template>
  <v-row>
    <v-col class="mt-2" cols="12" offset-lg="1" lg="10">
      <v-breadcrumbs :items="breadcrumbs" divider=">" class="pl-1 pb-3"/>
      <v-card>
        <v-card-title class="theme">
          <v-icon class="white--text">lock_open</v-icon>
          <span class="headline ml-2 white--text">Access management
            <v-tooltip right>
              <template v-slot:activator="{ on }">
                <v-icon small class="white--text" v-on="on">help</v-icon>
              </template>
              <div>Use this section to change and modify access for employees</div>
            </v-tooltip>
          </span>
          <v-spacer/>
          <span>
            <v-btn @click="changeAccess" depressed class="action rounded-sm white--text"
            >Assign Role</v-btn>
          </span>
        </v-card-title>
        <v-divider/>
        <v-card-text>

          <div class="body-2 ml-2 my-2">
            <div>Here you can see all the employees with extra access roles</div>
            <div class="mt-n3 float-right">
              <v-btn @click="accessLogs" small class="text-capitalize ml-n3 theme-text">check access logs</v-btn>
            </div>
            <div>
              <div class="subtitle-1 theme-text">Current roles</div>
              <div class="subtitle-2 theme-text mt-2">Admin:</div>
              <div>Can access everything including all employee data, control of company and all admin settings.</div>
              <div class="subtitle-2 theme-text mt-2">Managers:</div>
              <div>Can access detailed data of employees reporting directly to the and approve their time-off. Can
                publish announcements and backoffice items.
              </div>
              <div class="subtitle-2 theme-text mt-2">Employees:</div>
              <div>Default role. Can only access their own employee data.</div>

            </div>
          </div>

          <v-data-table
              :headers="headers"
              :items="hrOrManagers"
              class="elevation-0 row-pointer"
              hide-default-footer
              @click:row="changeCurrentEmpRole"
          >
            <template v-slot:footer>
              <div class="text-center mt-5">
                <v-btn
                    fab
                    small
                    depressed
                    class="theme"
                    @click="previous"
                    :disabled="pagination.page <= 0"
                >
                  <v-icon small>navigate_before</v-icon>
                </v-btn>
                <span>
                  <v-btn text :disabled="true">{{ pagination.page + 1 }}</v-btn>
                </span>
                <v-btn
                    fab
                    small
                    depressed
                    class="theme"
                    @click="next"
                    :disabled="pagination.disableNext"
                >
                  <v-icon small>navigate_next</v-icon>
                </v-btn>
              </div>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>
    </v-col>
    <v-dialog v-model="changeAccessDialog" persistent width="700px">
      <v-card>
        <v-card-title class="theme">
          <span class="headline white--text">Change access level for an employee</span>
          <v-spacer/>
          <v-btn icon @click="closeForm">
            <v-icon class="white--text">close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider/>
        <v-card-text>
          <v-form ref="addTimeoffForm" v-model="changeAccessFormValidation">
            <v-row>
              <v-col cols="12">
                <v-select
                    :items="employees"
                    :rules="[validationRules.required]"
                    v-model="employee"
                    :return-object="true"
                    :item-text="fullName"
                    persistent-hint
                    label="Select employee*"
                    clearable
                    class="mb-8 mt-4"
                    @change="currentRole"
                />
                <v-select
                    :items="roles"
                    :rules="[validationRules.required]"
                    v-model="from"
                    :return-object="true"
                    persistent-hint
                    label="Current role"
                    clearable
                    class="mb-8"
                    disabled
                />
                <v-select
                    :items="roles"
                    :rules="[validationRules.required]"
                    v-model="to"
                    :return-object="true"
                    persistent-hint
                    label="New role"
                    clearable
                />
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-divider/>
        <v-card-actions>
          <v-btn text class="action white--text rounded-sm" @click="submitForm">Submit</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="errorDialog" persistent width="500px">
      <v-card>
        <v-card-title class="theme">
          <span class="headline white--text">This operation is not allowed</span>
          <v-spacer/>
          <v-btn icon @click="errorDialog=false">
            <v-icon class="white--text">close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <div class="subtitle-1 mt-4">
            You cannot change your own role. This is to avoid losing the Admin access by mistake.
          </div>
        </v-card-text>
        <v-divider/>
      </v-card>
    </v-dialog>
    <v-snackbar v-model="snackbar" :bottom="true" :right="true">
      {{ snackbarMessage }}
      <v-btn color="pink" text @click="snackbar = false">Close</v-btn>
    </v-snackbar>
  </v-row>
</template>

<script>
import {mapGetters} from "vuex";

import {Actions} from "@/views/actions";
import {Breadcrumbs} from "@/views/breadcrumbs";
import {Component} from "@/views/component";
import {Constants} from "@/views/constants";
import {TableHeaders} from "@/views/headers";

export default {
  name: Component.Protected.Company.Access.List,
  data: () => ({
    breadcrumbs: Breadcrumbs.Access.List,
    headers: TableHeaders.Access.List,
    requests: [],
    pagination: {
      disableNext: true,
      page: 0,
    },
    search: {
      name: "",
      limit: 50,
      offset: 0,
    },
    employees: [],
    hrOrManagers: [],
    changeAccessDialog: false,
    changeAccessFormValidation: false,
    employee: null,
    to: null,
    from: null,
    roles: ["Admin", "Manager", "Employee"],
    errorDialog: false
  }),
  computed: {
    ...mapGetters(["userInfo", "accessRequests"]),
  },
  methods: {
    changeCurrentEmpRole(item) {
      if (parseInt(this.userInfo.userID) === parseInt(item.id)) {
        this.errorDialog = true
      } else {
        this.employee = item;
        this.currentRole();
        this.changeAccessDialog = true;
      }
    },
    accessLogs(){
      this.$router.push({name:Component.Protected.Company.Access.Logs})
    },
    setup() {
      this.getRequests();
      this.getEmployees();

    },
    fullName(item) {
      let t = this.titleCase(`${item.first_name} ${item.last_name}`)
      return `${t} (${item.work_email})`
    },
    getEmployees() {
      let search = {limit: 499, offset: 0, type: Constants.Employee.Type.Active}
      this.$store.dispatch(Actions.Employee.Find, search).then((r) => {
        this.employees = [];
        this.hrOrManagers = [];
        if (r.status === 200) {
          r.data.forEach(e => {
            if (parseInt(e.id) !== parseInt(this.userInfo.userID)) {
              this.employees.push(e)
            }
            if ((e.is_hr || e.is_manager)) {
              this.hrOrManagers.push({...e, role: this.calculateRole(e)})
            }
          })
        }
      }).catch((err) => {
        console.error(Constants.Employee.Messages.Error.Find, err);
        this.snackbarMessage = Constants.Employee.Messages.Error.Find;
        this.snackbar = true;
      });
    },
    closeForm() {
      this.employee = {}
      this.from = null
      this.to = null
      this.changeAccessDialog = false;
    },
    calculateRole(e) {
      let role = Constants.Roles.Employee
      if (e.is_hr) {
        role = "Admin"
      } else if (e.is_employee) {
        role = Constants.Roles.Employee
      } else if (e.is_manager) {
        role = Constants.Roles.Manager
      }
      return role
    },
    submitForm() {
      let request = {}
      if (this.$refs.addTimeoffForm.validate()) {
        request.user_id = parseInt(this.employee.id)
        request.user_email = this.employee.work_email
        request.from = this.from === "Admin" ? Constants.Roles.HR : this.from
        request.to = this.to === "Admin" ? Constants.Roles.HR : this.to
        request.changes_by_id = parseInt(this.userInfo.userID)
        request.changes_by_email = this.userInfo.email

        this.$store.dispatch(Actions.Auth.AddAccessRequest, request).then(r => {
          if (r.status === 200) {
            this.closeForm()
            this.setup();
            this.snackbarMessage = "User permission changed"
            this.snackbar = true;
          }
        }).catch(e => {
          this.snackbarMessage = "Error changing user permissions. Please contact LessAdmin "
          this.snackbar = true
        })
      }
    },
    changeAccess() {
      this.changeAccessDialog = true;
    },
    getRequests() {
      this.$store.dispatch(Actions.Auth.AccessRequestList, this.search)
    },
    currentRole() {
      let role = Constants.Roles.Employee
      if (this.employee.is_hr) {
        // role = Constants.Roles.HR
        role = "Admin"
      } else if (this.employee.is_employee) {
        role = Constants.Roles.Employee
      } else if (this.employee.is_manager) {
        role = Constants.Roles.Manager
      }
      this.from = role
    },
    next() {
      if (this.pagination.page < 0) {
        this.pagination.page = 0;
        this.search.offset = 0;
      } else if (this.requests.length >= this.search.limit) {
        this.pagination.disableNext = false;
        this.pagination.page++;
        this.search.offset = this.search.limit * this.pagination.page;
        this.getRequests();
      }
    },
    previous() {
      this.pagination.page--;
      if (this.pagination.page < 0) {
        this.pagination.page = 0;
        this.search.offset = 0;
      } else {
        this.pagination.disableNext = false;
        this.search.offset = this.search.limit * this.pagination.page;
        this.getRequests();
      }
    },
  },
  created() {
    if (!this.userInfo || this.userInfo.role !== 'HR') {
      this.$router.go(-1)
    }
    this.setup();
  }
}
</script>

<style scoped>
.v-tooltip__content {
  margin-left: 0 !important;
  background: #fff;
  color: #551738;
}
</style>